import React, { FC, useEffect, useState } from "react";
import { codeCatalogNavItem } from "../../../components/appbar/menu/const";
import NavBar from "../../../components/navbar/NavBar";
import { useNavigate, useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { boldFontFamily } from "../../../theme";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import useTranslation from "../../../hooks/usetranslation";
import useAsset from "../../../hooks/useasset";
import { Button, Dialog, DialogContent } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { mappedLocales, routes } from "../../../lib/utils";
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon, DownloadIcon } from "../../../components/icons/MANIcons";
import { Theme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import { CodeItemImagesContent, useCodeItemDetailsQuery } from "../../../graphql";
import GqlError from "../../../components/error/GqlError";
import Spinner from "../../../components/Spinner";
import useLocale from "../../../hooks/uselocale";
import CodeDetailsContent from "./CodeDetailsContent";
import CodeDetailContentNav from "./CodeDetailContentNav";
import Divider from "@mui/material/Divider";
import AssetItem from "../../../components/asset/AssetItem";
import CodeDetailsRelatedContentNewsItem from "./CodeDetailsRelatedContentNewsItem";
import CodeDetailsRelatedContentContentSimple from "./CodeDetailsRelatedContentContentSimple";
import CodeDetailsRelatedContentCmsSubpage from "./CodeDetailsRelatedContentCmsSubpage";
import CodeDetailsRelatedContentPodcast from "./CodeDetailsRelatedContentPodcast";
import PreviewTag from "../../../components/PreviewTag";

const useStyles = makeStyles(() =>
    createStyles({
        codeDetailsRoot: {
            "@media print": {
                // Only target elements inside .root
                "& nav, & .MuiButton-root, & .MuiAppBar-root, & .MuiDrawer-root": {
                    display: "none !important"
                },
                "& .MuiContainer-root, & .MuiGrid-root": {
                    width: "100% !important",
                    maxWidth: "100% !important"
                }
            }
        },
        sectionItem: {
            "@media print": {
                breakInside: "avoid",
                pageBreakInside: "avoid",
                "& .relatedCodeContent": {
                    display: "none !important"
                }
            }
        },
        printHide: {
            "@media print": {
                display: "none !important"
            }
        },
        disclaimer: {
            fontSize: 14
        },
        metadata: {
            display: "none",
            "@media print": {
                display: "block !important",
                width: "100%",
                fontSize: 13,
                pageBreakBefore: "always",
                pageBreakAfter: "avoid"
            }
        },
        divider: {
            margin: "20px 0",
            "@media print": {
                display: "none !important"
            }
        }
    })
);

const ASSET_SIZE = 110;

const CodeDetailsView: FC = () => {
    const { code } = useParams();
    const { t } = useTranslation();
    const { locale } = useLocale();
    const [{ data, fetching, error }] = useCodeItemDetailsQuery({
        variables: {
            id: code ?? "",
            locale: mappedLocales(locale)
        }
    });
    const navigate = useNavigate();
    const classes = useStyles();
    const { createImageUrl } = useAsset();
    const [current, setCurrent] = useState<CodeItemImagesContent | undefined>();
    const [thumbnailStart, setThumbnailStart] = useState(0);
    const thumbnailsToShow = 4;
    const totalThumbnails = data?.codeItemDetails?.data.images.length ?? 0;
    const [imageModalOpen, setImageModalOpen] = useState(false);

    const handleOpenImageModal = () => setImageModalOpen(true);
    const handleCloseImageModal = () => setImageModalOpen(false);

    const currentImageSrc = createImageUrl(
        current?.imagePath?.replace("/image?path=", "")
    );


    useEffect(() => {
        const firstImage = data?.codeItemDetails?.data?.images?.[0];
        if (firstImage && !current) {
            setCurrent(firstImage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
    if (error) {
        return <GqlError error={error} />;
    }
    if (fetching) {
        return <Spinner />;
    }

    return (
        <Box sx={{ mb: 2 }} className={classes.codeDetailsRoot}>
            <NavBar links={[{
                label: t(codeCatalogNavItem.id as string),
                to: "/codes"
            }, { label: data?.codeItemDetails?.data?.code }]} />
            <Box sx={{ backgroundColor: "#303C49" }}>
                <Container sx={{ paddingTop: 4, paddingBottom: 6 }}>
                    <PreviewTag isPreview={data?.codeItemDetails?.data?.isPreview} sx={{ mb: 1 }} />
                    <Typography fontFamily={boldFontFamily} fontSize={35} color="white" textTransform="uppercase">
                        {data?.codeItemDetails?.data?.title}
                    </Typography>
                    <Typography color="#CBD3DC" width={{ sx: "100%", md: "70%" }}
                                dangerouslySetInnerHTML={{ __html: data?.codeItemDetails?.data?.description ?? "" }} />
                </Container>
            </Box>
            <CodeDetailContentNav
                sections={data?.codeItemDetails?.data?.sections}
                backgroundColor={"white"} />
            <Container disableGutters>
                {data?.codeItemDetails?.data?.images && (data?.codeItemDetails?.data?.images?.length > 0) && (
                    <>
                        <Box
                            sx={{
                                my: 1,
                                overflow: "hidden",
                                borderRadius: 1
                            }}
                        >
                            <Box
                                component="img"
                                src={currentImageSrc}
                                alt={current?.id}
                                onClick={handleOpenImageModal}
                                sx={{
                                    width: "100%",
                                    height: 500,
                                    objectFit: "contain",
                                    transition: "opacity 0.3s ease-in-out",
                                    cursor: "zoom-in"
                                }}
                            />
                        </Box>
                        <Dialog
                            open={imageModalOpen}
                            onClose={handleCloseImageModal}
                            maxWidth={false}
                            PaperProps={{
                                sx: {
                                    width: "fit-content",
                                    maxWidth: "none",
                                    maxHeight: "90vh",
                                    m: 0,
                                    borderRadius: 1,

                                    backgroundColor: "black",
                                    backgroundImage: "none",

                                    display: "flex",
                                    flexDirection: "row",
                                    overflow: "hidden"
                                }
                            }}
                            slotProps={{
                                backdrop: {
                                    sx: {
                                        backgroundColor: "rgba(0,0,0,0.75)"
                                    }
                                }
                            }}
                        >
                            {/* Image area (takes all remaining space) */}
                            <DialogContent
                                sx={{
                                    p: 0,
                                    flex: "1 1 auto",
                                    minWidth: 0,
                                    backgroundColor: "black",
                                    overflowY: "auto",
                                    overflowX: "hidden"
                                }}
                            >
                                <Box
                                    component="img"
                                    src={currentImageSrc}
                                    alt={current?.id}
                                    sx={{
                                        width: "auto",
                                        height: "auto",
                                        minHeight: "500px",
                                        objectFit: "contain",
                                        display: "block"
                                    }}
                                />
                            </DialogContent>

                            <Box
                                sx={{
                                    width: 44,
                                    flex: "0 0 auto",
                                    backgroundColor: "black",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    pt: 0.5,
                                    pr: 0.5
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleCloseImageModal}
                                    aria-label="close"
                                    sx={{
                                        p: 0.5, // shrink the button footprint
                                        color: "white",
                                        backgroundColor: "rgba(255,255,255,0.10)", // subtle background behind icon
                                        "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" }
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>

                        </Dialog>


                        <Typography className={classes.disclaimer} variant="h5" sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 1
                        }}>{t("code_catalogue_detail_disclaimer")}</Typography>
                        {/* Thumbnails row */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 1,
                                overflow: "hidden",
                                px: 1
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => setThumbnailStart((prev) => Math.max(prev - 1, 0))}
                                disabled={thumbnailStart === 0}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            {data?.codeItemDetails?.data?.images
                                .slice(thumbnailStart, thumbnailStart + thumbnailsToShow)
                                .map((image, idx) => (
                                    <Box
                                        key={idx}
                                        component="img"
                                        src={createImageUrl(image?.imagePath?.replace("/image?path=", ""))}
                                        alt={`Thumbnail ${thumbnailStart + idx + 1}`}
                                        onMouseEnter={() => setCurrent(image)}
                                        sx={(theme: Theme) => ({
                                            width: 100,
                                            height: 100,
                                            objectFit: "cover",
                                            borderRadius: 1,
                                            cursor: "pointer",
                                            flexShrink: 0,
                                            border:
                                                image?.id === current?.id
                                                    ? `2px solid ${theme.palette.primary.main}`
                                                    : `1px solid ${theme.palette.grey[300]}`,
                                            transition: "transform 0.2s",
                                            "&:hover": {
                                                transform: "scale(1.05)"
                                            }
                                        })}
                                    />
                                ))}
                            <IconButton
                                size="small"
                                onClick={() =>
                                    setThumbnailStart((prev) =>
                                        Math.min(prev + 1, totalThumbnails - thumbnailsToShow)
                                    )
                                }
                                disabled={thumbnailStart >= totalThumbnails - thumbnailsToShow}
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </>
                )}

                <Grid container direction="row" sx={{ pt: 4 }}>
                    <Grid item xs={12} className={classes.metadata}>
                        {data?.codeItemDetails?.data?.metadata && (
                            <>
                                {!!data?.codeItemDetails?.data?.metadata.series?.length &&
                                    <Stack direction="row" spacing={2} sx={{ mb: 2, px: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                            {t("codeCatalog_series")}
                                        </Typography>
                                        <Typography>
                                            {data?.codeItemDetails?.data?.metadata.series.join(", ")}
                                        </Typography>
                                    </Stack>
                                }
                                {!!data?.codeItemDetails?.data?.metadata.busType?.length &&
                                    <Stack direction="row" spacing={2} sx={{ mb: 2, px: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                            {t("codeCatalog_type")}
                                        </Typography>
                                        <Typography>
                                            {data?.codeItemDetails?.data?.code}
                                        </Typography>
                                    </Stack>
                                }
                                {!!data?.codeItemDetails?.data?.metadata.genre?.length &&
                                    <Stack direction="row" spacing={2} sx={{ mb: 2, px: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                            {t("codeCatalog_codeNumber")}
                                        </Typography>
                                        <Typography>
                                            {data?.codeItemDetails?.data?.metadata.genre.join(", ")}
                                        </Typography>
                                    </Stack>
                                }
                                {data?.codeItemDetails?.data?.metadata.availableFrom &&
                                    <Stack direction="row" spacing={2} sx={{ mb: 2, px: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                            {t("code_details_availableFrom")}
                                        </Typography>
                                        <Typography>
                                            {data?.codeItemDetails?.data?.metadata.availableFrom}
                                        </Typography>
                                    </Stack>}
                            </>
                        )}
                    </Grid>
                    <Grid item xs={8.5}>
                        {data?.codeItemDetails?.data?.sections && (
                            <CodeDetailsContent sections={data?.codeItemDetails?.data?.sections}
                                                className={classes.sectionItem} />
                        )}
                        <Divider className={classes.divider} />

                        <Stack sx={{ width: "fit-content", m: 2 }} spacing={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => window.print()}
                                startIcon={<DownloadIcon />}
                            >
                                Download PDF
                            </Button>
                            <Button onClick={() => navigate(routes.codeCatalog.root)}
                                    sx={{ backgroundColor: "transparent" }}
                                    startIcon={<ChevronRightIcon sx={{ color: "secondary.main" }} />}>
                                {t("back_to_overview")}
                            </Button>
                        </Stack>

                    </Grid>
                    <Grid item xs={3.5} container className={classes.printHide} direction="column" sx={{ px: 2 }}>
                        {/* Related Content Section */}
                        <Stack direction="column" spacing={2} pl={1}>
                            <Grid item xs={12}>
                                {data?.codeItemDetails?.data?.metadata && (
                                    <>
                                        {!!data?.codeItemDetails?.data?.metadata.series?.length &&
                                            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                    {t("codeCatalog_series")}
                                                </Typography>
                                                <Typography>
                                                    {data?.codeItemDetails?.data?.metadata.series.join(", ")}
                                                </Typography>
                                            </Stack>
                                        }
                                        {!!data?.codeItemDetails?.data?.metadata.busType?.length &&
                                            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                    {t("codeCatalog_type")}
                                                </Typography>
                                                <Typography>
                                                    {data?.codeItemDetails?.data?.metadata.busType.join(", ")}
                                                </Typography>
                                            </Stack>
                                        }
                                        {!!data?.codeItemDetails?.data?.metadata.genre?.length &&
                                            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                    {t("codeCatalog_codeNumber")}
                                                </Typography>
                                                <Typography>
                                                    {data?.codeItemDetails?.data?.code}
                                                </Typography>
                                            </Stack>
                                        }
                                        {data?.codeItemDetails?.data?.metadata.availableFrom &&
                                            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                                    {t("code_details_availableFrom")}
                                                </Typography>
                                                <Typography>
                                                    {data?.codeItemDetails?.data?.metadata.availableFrom}
                                                </Typography>
                                            </Stack>}
                                    </>
                                )}
                            </Grid>
                            {Boolean(data?.codeItemDetails?.data?.relatedContents?.length) && (
                                <>
                                    <Grid item>
                                        <Typography variant="h3" sx={{ mb: 2 }}>
                                            {t("related_content")}
                                        </Typography>
                                    </Grid>
                                    {data?.codeItemDetails?.data.relatedContents.map((related) => {
                                        switch (related?.__typename) {
                                            case "News":
                                                return (
                                                    <Grid key={related.newsId} item>
                                                        <CodeDetailsRelatedContentNewsItem
                                                            title={related.newsTitle ?? ""}
                                                            thumbnail={related.thumbnail}
                                                            description={related.description}
                                                            originalPath={related.originalPath}
                                                            isPreview={related.isPreview}
                                                        />
                                                    </Grid>
                                                );
                                            case "ContentSimple":
                                                return (
                                                    <Grid key={related.contentSimpleId} item>
                                                        <CodeDetailsRelatedContentContentSimple
                                                            title={related.name}
                                                            thumbnail={related.imagePath}
                                                            description={related.intro}
                                                            originalPath={related.originalPath}
                                                        />
                                                    </Grid>
                                                );
                                            case "CmsSubPage":
                                                return (
                                                    <Grid key={related.cmsSubPageId} item>
                                                        <CodeDetailsRelatedContentCmsSubpage
                                                            title={related.cmsSubPageTitle}
                                                            originalPath={related.originalPath}
                                                            contentBlock={related?.contentBlocks?.find((item) => item.content)?.content}
                                                            isPreview={related.isPreview}
                                                        />
                                                    </Grid>
                                                );
                                            case "Podcast":
                                                return (
                                                    <Grid key={related.podcastId} item>
                                                        <CodeDetailsRelatedContentPodcast
                                                            title={related.title}
                                                            thumbnail={related.thumbnail}
                                                            description={related.description}
                                                            originalPath={related.originalPath}
                                                        />
                                                    </Grid>
                                                );
                                            default:
                                                return null;
                                        }
                                    })}
                                </>
                            )}
                        </Stack>
                        {/* Assets Section */}
                        {Boolean(data?.codeItemDetails?.data?.relatedAssets?.length) && (
                            <Stack direction="column" sx={{ mt: 3 }} pl={1}>
                                <Grid container direction="row" spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h3" sx={{ my: 2 }}>
                                            {"Assets"}
                                        </Typography>
                                    </Grid>
                                    {data?.codeItemDetails?.data.relatedAssets.map((asset, idx) => (
                                        <Grid item xs={6} key={idx}>
                                            <AssetItem
                                                key={asset?.id}
                                                id={asset?.id ?? ""}
                                                title={asset?.title}
                                                type={asset?.type}
                                                isPreview={asset?.isPreview}
                                                assetHeight={ASSET_SIZE}
                                                showTitle={false}
                                                showAdditionalIcons={false}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Stack>
                        )}
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
};
export default CodeDetailsView;
